import { Component, Event, EventEmitter, h, Prop } from '@stencil/core';

import { Appearance } from '@entities/Appearance';
import { Configuration } from '@entities/Configuration';
import { Design } from '@entities/Design';
import { ServiceType } from '@entities/ServiceType';
import { TransactionalServiceType } from '@entities/TransactionalServiceType';
import { WidgetPosition } from '@entities/WidgetPosition';
import appStore from '@stores/app.store';

@Component({
  tag: 'wog-tabbed-widget',
})
export class TabbedWidget {
  @Event() tabButtonClick: EventEmitter;
  @Prop() collapsed = false;
  @Prop() widgetLabel: string;
  @Prop() isBRTOnly?: boolean = false;

  private handleClick = () => {
    this.tabButtonClick.emit();
  };

  private handleKeyEnter = e => {
    if (e.key === 'Enter' || e.key === ' ') {
      //enter or spacebar
      this.tabButtonClick.emit();
    }
  };

  private getStyles(config: Configuration, textMessage: string) {
    let translateY = 155;

    if (
      [
        'Rate your experience',
        'Rate this application',
        'Rate this service',
        'Help us improve',
      ].includes(textMessage)
    ) {
      translateY = 166;
    } else if (
      ['Rate this service', 'Rate this payment', 'Rate this booking'].includes(textMessage)
    ) {
      translateY = 166;
    } else if (textMessage === 'Feedback') {
      translateY = 100;
    } else {
      translateY = 166;
    }

    let styles: { [key: string]: string } = {
      backgroundColor: config.themeColor,
      color: config.appearance === Appearance.Light ? 'white' : '#333333',
    };

    if (config.design === Design.TabDesign) {
      if (config.position === WidgetPosition.MiddleLeft) {
        return {
          ...styles,
          transform: `rotate(90deg) translateX(0%) translateY(${translateY}%)`,
          transformOrigin: 'center center',
        };
      } else if (config.position === WidgetPosition.MiddleRight) {
        return {
          ...styles,
          transform: `rotate(90deg) translateX(0%) translateY(-${translateY}%)`,
          transformOrigin: 'center center',
        };
      }
    }

    if (config.design === Design.TabDesignWithSmiley) {
      translateY = translateY + 40;
      styles = {
        ...styles,
      };

      if (config.position === WidgetPosition.MiddleLeft) {
        return {
          ...styles,
          transform: `rotate(90deg) translateX(0%) translateY(${translateY}%)`,
          transformOrigin: 'center center',
        };
      } else if (config.position === WidgetPosition.MiddleRight) {
        return {
          ...styles,
          transform: `rotate(90deg) translateX(0%) translateY(-${translateY}%)`,
          transformOrigin: 'center center',
        };
      }
    }

    return styles;
  }

  private getFaceIcon(config: Configuration) {
    const styles = { margin: 'auto' };
    if (
      config.position === WidgetPosition.MiddleLeft ||
      config.position === WidgetPosition.MiddleRight
    ) {
      return { ...styles, transform: 'rotate(-90deg)' };
    }
    return styles;
  }

  private renderWidgetLabel = (
    serviceType: ServiceType,
    transactionalServiceType: TransactionalServiceType,
  ) => {
    if (serviceType === ServiceType.InformationalService) {
      return this.widgetLabel;
    } else {
      switch (transactionalServiceType) {
        case TransactionalServiceType.ApplicationOrRenewal:
          return 'Rate this application';

        case TransactionalServiceType.Payment:
          return 'Rate this payment';

        case TransactionalServiceType.BookingsOrAppointments:
          return 'Rate this booking';

        case TransactionalServiceType.InformationalService:
          return 'Rate this service';

        default:
          return this.widgetLabel;
      }
    }
  };

  private renderImage(appearance: Appearance) {
    if (appearance === Appearance.Light) {
      return (
        <svg
          fill="none"
          class="wog--h-6 wog--w-6"
          viewBox="0 0 49 49"
          xmlns="http://www.w3.org/2000/svg">
          <g clip-rule="evenodd" fill="#fff" fill-rule="evenodd">
            <path
              d="m48.478 24.46c0 13.2548-10.7451 24-24 24-13.2548 0-23.999973-10.7452-23.999973-24 0-13.2549 10.745173-24.000039 23.999973-24.000039 13.2549 0 24 10.745139 24 24.000039zm-23.9999 18.1223c9.9932 0 18.1224-8.1292 18.1224-18.1225 0-9.9932-8.1292-18.12243-18.1224-18.12243s-18.12245 8.12923-18.12245 18.12243c0 9.9933 8.12925 18.1225 18.12245 18.1225z"
              fill-opacity=".5"
            />
            <path d="m24.4781 42.5823c9.9932 0 18.1224-8.1292 18.1224-18.1225 0-9.9932-8.1292-18.12243-18.1224-18.12243s-18.12245 8.12923-18.12245 18.12243c0 9.9933 8.12925 18.1225 18.12245 18.1225zm-10.3559-12.6338c2.8478 3.0031 6.5759 4.5047 10.3557 4.5047 3.728 0 7.5079-1.5016 10.3557-4.5047.466-.5178.466-1.2945 0-1.8123s-1.2427-.5178-1.7087 0c-4.7636 5.0225-12.5304 4.9708-17.294 0-.466-.5178-1.2427-.5178-1.7087 0s-.466 1.2945 0 1.8123zm3.3137-6.5757c-1.2427 0-2.2265-.8802-2.2265-1.9158v-1.2945c0-1.0873.9838-1.9158 2.2265-1.9158s2.2265.8803 2.2265 1.9158v1.2945c0 1.0356-.9838 1.9158-2.2265 1.9158zm11.8568-1.9158c0 1.0356.9838 1.9158 2.2264 1.9158 1.1909 0 2.2265-.8802 2.2265-1.9158v-1.2945c0-1.0355-.9838-1.9158-2.2265-1.9158-1.2426 0-2.2264.8285-2.2264 1.9158z" />
          </g>
        </svg>
      );
    }
    return (
      <svg
        fill="none"
        class="wog--h-6 wog--w-6"
        viewBox="0 0 48 48"
        xmlns="http://www.w3.org/2000/svg">
        <path
          clip-rule="evenodd"
          d="m48 24c0 13.2548-10.7452 24-24 24s-24-10.7452-24-24 10.7452-24 24-24 24 10.7452 24 24zm-5.8775-.0002c0 9.9932-8.1292 18.1225-18.1224 18.1225-9.9933 0-18.12248-8.1293-18.12248-18.1225s8.12918-18.12243 18.12248-18.12243c9.9932 0 18.1224 8.12923 18.1224 18.12243zm-18.1229 9.9932c-3.7799 0-7.5079-1.5015-10.3557-4.5047-.466-.5178-.466-1.2944 0-1.8122s1.2427-.5178 1.7087 0c4.7636 4.9707 12.5303 5.0225 17.294 0 .466-.5178 1.2426-.5178 1.7086 0 .4661.5178.4661 1.2944 0 1.8122-2.8478 3.0032-6.6276 4.5047-10.3556 4.5047zm-9.2682-12.996c0 1.0355.9838 1.9158 2.2265 1.9158 1.2426 0 2.2264-.8803 2.2264-1.9158v-1.2945c0-1.0356-.9838-1.9158-2.2264-1.9158-1.2427 0-2.2265.8285-2.2265 1.9158zm16.3097 1.9158c-1.2427 0-2.2265-.8803-2.2265-1.9158v-1.2945c0-1.0873.9838-1.9158 2.2265-1.9158s2.2265.8802 2.2265 1.9158v1.2945c0 1.0355-1.0356 1.9158-2.2265 1.9158z"
          fill="#333"
          fill-rule="evenodd"
        />
      </svg>
    );
  }

  private renderButton(configuration: Configuration) {
    const buttonAriaLabel = `${this.widgetLabel} popup`;
    const styles =
      configuration.position === WidgetPosition.MiddleRight
        ? { transform: 'scale(-1, 1) scale(1, -1)' }
        : {};

    const serviceType = appStore.get('serviceType');
    const transactionalServiceType = appStore.get('transactionalServiceType');

    const textMessage = this.isBRTOnly ? this.widgetLabel : this.renderWidgetLabel(serviceType, transactionalServiceType);

    return (
      <div
        class={{
          'wog--tabbed-button': true,
          'wog--tabbed-button-bottom-left': configuration.position === WidgetPosition.BottomLeft,
          'wog--tabbed-button-bottom-right': configuration.position === WidgetPosition.BottomRight,
          'wog--tabbed-button-side-left': configuration.position === WidgetPosition.MiddleLeft,
          'wog--tabbed-button-side-right': configuration.position === WidgetPosition.MiddleRight,
        }}
        tabindex={0}
        onKeyDown={this.handleKeyEnter.bind(this)}
        onClick={this.handleClick.bind(this)}
        style={this.getStyles(configuration, textMessage)}
        role="button">
        <p
          class={{
            'wog--text-foundation': true,
            'wog--mr-2': configuration.design === Design.TabDesignWithSmiley,
            'wog--font-bold': true,
          }}
          aria-label={buttonAriaLabel}
          style={styles}>
          {textMessage}
        </p>
        {configuration.design === Design.TabDesignWithSmiley && (
          <div style={this.getFaceIcon(configuration)} class="wog--h-6 wog--w-6 wog--origin-center">
            {this.renderImage(configuration.appearance)}
          </div>
        )}
      </div>
    );
  }

  private renderIcon(position: WidgetPosition, appearance: Appearance) {
    const colorClass =
      appearance === Appearance.Light ? 'wog--text-white' : 'wog--text-theme-black';
    switch (position) {
      case WidgetPosition.MiddleLeft: {
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class={colorClass}>
            <polyline points="9 18 15 12 9 6" />
          </svg>
        );
      }
      case WidgetPosition.MiddleRight: {
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class={colorClass}>
            <polyline points="15 18 9 12 15 6" />
          </svg>
        );
      }
      case WidgetPosition.BottomRight:
      case WidgetPosition.BottomLeft: {
        return (
          <svg
            style={{
              display: 'block',
              margin: 'auto',
            }}
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class={colorClass}>
            <polyline points="18 15 12 9 6 15" />
          </svg>
        );
      }
    }
  }

  private renderCollapsedButton(configuration: Configuration) {
    const rootStyles = {
      backgroundColor: configuration.themeColor,
      color: configuration.appearance === Appearance.Light ? 'white' : '#333333',
    };
    return (
      <div
        style={rootStyles}
        class={{
          'wog--feedback-tab': true,
          'wog--feedback-tab-bottom-left': configuration.position === WidgetPosition.BottomLeft,
          'wog--feedback-tab-bottom-right': configuration.position === WidgetPosition.BottomRight,
          'wog--feedback-tab-side-left': configuration.position === WidgetPosition.MiddleLeft,
          'wog--feedback-tab-side-right': configuration.position === WidgetPosition.MiddleRight,
        }}
        tabindex={0}
        onKeyDown={this.handleKeyEnter.bind(this)}
        onClick={this.handleClick.bind(this)}>
        <div
          style={rootStyles}
          class={{
            'wog--feedback-notch': true,
            'wog--text-white': true,
            'wog--feedback-notch-bottom-left': configuration.position === WidgetPosition.BottomLeft,
            'wog--feedback-notch-bottom-right':
              configuration.position === WidgetPosition.BottomRight,
            'wog--feedback-notch-side-left': configuration.position === WidgetPosition.MiddleLeft,
            'wog--feedback-notch-side-right': configuration.position === WidgetPosition.MiddleRight,
          }}>
          {this.renderIcon(configuration.position, configuration.appearance)}
        </div>
      </div>
    );
  }

  render() {
    const configuration = appStore.get('configuration');
    return (
      <div class="wog--tabbed-button-container">
        {this.collapsed
          ? this.renderCollapsedButton(configuration)
          : this.renderButton(configuration)}
      </div>
    );
  }
}
